import {
  ApolloClient,
  MutationTuple,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  css,
  Device,
  IconName,
  IFalconTheme,
  InlineDateRangePicker,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  useDeviceViewport,
  withTheme,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as moment from 'moment';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { TranslationsContext } from '../../common/contexts';
import { ApplicationContext, IApplication } from '../../common/contexts/ApplicationContext';
import { ICalendarEntry, ICalendarSelectionSummary, IPageName } from '../../common/types/apiSchema';
import { getLocaleSettings } from '../../common/utils/getLocaleSettingsUtils';
import { getPaths } from '../../constants/paths/paths';
import { useResolvers } from '../../utils/ApolloUtils';
import {
  IUpdateTravelPeriodDatesDraftVariables,
  RESET_TRAVEL_PERIOD,
  SHOW_DEALS,
  UPDATE_TRAVEL_PERIOD_DATES_DRAFT,
} from './BookingAmendments.mutations';
import {
  GET_CALENDAR,
  GET_CALENDAR_SELECTION_SUMMARY,
  GET_FILTERS_DATA,
  ITravelPeriod,
  ITravelPeriodDraft,
} from './BookingAmendments.queries';
import { resolvers } from './BookingAmendments.resolvers';
import {
  BookingAmendmentTextInput,
  BookingAmendmentTextInputWithMargin,
  CalendarContainer,
  Container,
  TextInputContainer,
  TitleContainer,
} from './BookingAmendments.styles';
import { buildLabelsForTravelPeriodSection } from './BookingAmendments.utils';
import { CalendarInfo } from './CalendarInfo';

export interface IBookingAmendmentsProps {
  className?: string;
  theme?: IFalconTheme;
  locale?: string;
  isExpandable?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  travelPeriod?: ITravelPeriod;
  travelPeriodDraft?: ITravelPeriodDraft;
  saleId: string;
  offerId: string;
  rooms: number;
  adults: number;
  currency: string;
  affiliate: string;
  originalStartDate?: moment.Moment;
  originalEndDate?: moment.Moment;
}

const TODAY: moment.Moment = moment(new Date());
const YESTERDAY: moment.Moment = moment(new Date()).subtract(1, 'days');
const NEXT_YEAR: moment.Moment = moment(new Date()).add(1, 'years');
const CALENDAR_DAY_SIZE_MOBILE: number = 43;
const CALENDAR_DAY_SIZE_DESKTOP: number = 50;

const BookingAmendments: React.FunctionComponent<IBookingAmendmentsProps> = ({
  className,
  locale = 'en-GB',
  travelPeriod: { startDate, endDate },
  travelPeriodDraft,
  theme,
  saleId,
  offerId,
  rooms,
  adults,
  currency,
  affiliate,
  originalStartDate,
  originalEndDate,
}) => {
  const client: ApolloClient<{}> = useApolloClient();
  const deviceViewport: Device = useDeviceViewport();
  const isMobile: boolean = deviceViewport === Device.mobile;
  const isDesktop: boolean = deviceViewport === Device.desktop;
  const applicationProps: IApplication = React.useContext(ApplicationContext);
  const territory: string = getLocaleSettings(applicationProps.userTerritoryCode);

  React.useEffect(() => {
    client.writeQuery({
      data: {
        travelPeriod: {
          __typename: 'TravelPeriod',
          endDate: null,
          hasInitialisedData: false,
          startDate: null,
        },
        travelPeriodDraft: {
          __typename: 'TravelPeriodDraft',
          endDate: null,
          startDate: null,
        },
      },
      query: GET_FILTERS_DATA,
    });
  }, [isMobile]);

  const { bookingId = null } = useParams<{ bookingId: string }>();

  const { startDate: startDateDraft, endDate: endDateDraft } = travelPeriodDraft;
  const [defaultDateSelected, setDefaultDateSelected] = React.useState(true);

  const { data } = useQuery(GET_CALENDAR, {
    variables: {
      affiliate,
      currency,
      offerId,
      rooms,
      saleId,
      territory,
    },
  });

  const [getCalendarSelectionSummary, { data: calendarSelectionData }] = useLazyQuery(
    GET_CALENDAR_SELECTION_SUMMARY
  );

  const calendarSelectionSummary: ICalendarSelectionSummary =
    calendarSelectionData?.sale?.offer?.calendarSelectionSummary;

  React.useEffect(() => {
    if (checkOutLabel) {
      getCalendarSelectionSummary({
        context: { protectedFlashsales: true },
        variables: {
          affiliate,
          checkin: checkInLabel,
          checkout: checkOutLabel,
          currency,
          offerId,
          rebookedReservationId: bookingId,
          rooms,
          saleId,
          territory,
        },
      });
    }
  }, [endDateDraft]);

  let calendarEntries: ICalendarEntry[] = [];

  if (data?.sale?.offer?.calendar?.calendarEntries) {
    calendarEntries = data.sale.offer.calendar.calendarEntries;
  }

  const [updateDates]: MutationTuple<{}, IUpdateTravelPeriodDatesDraftVariables> = useMutation(
    UPDATE_TRAVEL_PERIOD_DATES_DRAFT
  );

  const [showDeals]: MutationTuple<{}, {}> = useMutation(SHOW_DEALS);
  const [resetTravelPeriod]: MutationTuple<{}, {}> = useMutation(RESET_TRAVEL_PERIOD);

  const { translations } = React.useContext(TranslationsContext);

  const [focusedDate, setFocusedDate] = React.useState(
    'startDate' as 'startDate' | 'endDate' | null
  );

  const [firstAvailableMonth] = React.useState<string | null>(null);
  const [lastAvailableMonth] = React.useState<string | null>(null);

  const handleDatesChange: ({
    startDate,
    endDate,
  }: {
    startDate: moment.Moment;
    endDate: moment.Moment;
  }) => void = ({ startDate: newStartDate, endDate: newEndDate }) => {
    updateDates({
      variables: { startDate: newStartDate, endDate: defaultDateSelected ? null : newEndDate },
    });
    setDefaultDateSelected(false);
  };

  const handleFocusChange: (arg: 'startDate' | 'endDate' | null) => void = newFocusedDate => {
    setFocusedDate(newFocusedDate || 'endDate');
  };

  const handleResetTravelPeriod: () => void = () => {
    setDefaultDateSelected(false);
    resetTravelPeriod();
    setFocusedDate('startDate');
  };

  useResolvers(resolvers);

  React.useMemo(() => {
    moment.updateLocale(locale, {
      week: {
        dow: 1,
        doy: 1,
      },
    });
  }, [locale]);

  const travelPeriodValueLabelArray: string[] = buildLabelsForTravelPeriodSection(
    startDate,
    endDate,
    locale
  );
  const travelPeriodValueLabel: string = travelPeriodValueLabelArray.join(', ');

  const returnFirstAvailableMonth: () => moment.Moment = () => {
    return firstAvailableMonth ? moment(firstAvailableMonth, 'YYYYMM').locale(locale) : TODAY;
  };

  const returnLastAvailableMonth: () => moment.Moment = () => {
    return lastAvailableMonth ? moment(lastAvailableMonth, 'YYYYMM').locale(locale) : NEXT_YEAR;
  };

  const checkInLabel: string = defaultDateSelected
    ? moment(originalStartDate)
        .locale(locale)
        .format('YYYY-MM-DD')
    : startDateDraft
    ? moment(startDateDraft)
        .locale(locale)
        .format('YYYY-MM-DD')
    : '';

  const checkOutLabel: string = defaultDateSelected
    ? moment(originalEndDate)
        .locale(locale)
        .format('YYYY-MM-DD')
    : endDateDraft
    ? moment(endDateDraft)
        .locale(locale)
        .format('YYYY-MM-DD')
    : '';
  const url: string = getPaths(IPageName.bookingsPage)[0];

  const handleShowDeals: () => void = () => {
    if (!endDateDraft) {
      handleDatesChange({
        endDate: moment(startDateDraft, 'YYYY-MM-DD').add(1, 'days'),
        startDate: moment(startDateDraft, 'YYYY-MM-DD'),
      });
    }
    showDeals();

    // Old booking form still takes ids without prepending "A"
    const urlOfferId: string = offerId.substring(1);
    const urlSaleId: string = saleId.substring(1);
    const urlBookingId: string = bookingId.substring(1);
    // NumberOfAdults is in fact adults per rooms
    const adultsPerRoom: number = adults / rooms;
    const rateCodes: string[] =
      calendarSelectionData?.sale?.offer?.calendarSelectionSummary?.rateCodes;
    const rateCodesString: string = rateCodes.map(rateCode => `&rateCodes=${rateCode}`).join('');
    window.location.assign(
      `/sale/book-hotel?startDate=${checkInLabel}&endDate=${checkOutLabel}&rooms=${rooms}&numberOfAdults=${adultsPerRoom}&offerId=${urlOfferId}&saleId=${urlSaleId}&originalReservationId=${urlBookingId}${rateCodesString}`
    );
    window.scrollTo(0, 0);
  };

  const handleCancel: () => void = () => {
    window.location.assign(`${url}/${bookingId}/details`);

    window.scrollTo(0, 0);
  };

  const getCalendarEntry: (day: moment.Moment) => ICalendarEntry | null = day => {
    const formattedDate: string = day.format('YYYY-MM-DD');

    return calendarEntries.find(entry => entry.date === formattedDate);
  };

  const isStartDateDisabled: (calendarEntry: ICalendarEntry, day: moment.Moment) => boolean = (
    calendarEntry,
    day
  ) =>
    day.isSameOrBefore(YESTERDAY) ||
    !calendarEntry ||
    calendarEntry?.closedToArrival ||
    calendarEntry.checkOutDayOnly;

  const isEndDateDisabled: (calendarEntry: ICalendarEntry, day: moment.Moment) => boolean = (
    calendarEntry,
    day
  ) => {
    const selectedStartDate: moment.Moment = moment(travelPeriodDraft?.startDate).startOf('day');
    const startCalendarEntry: ICalendarEntry = getCalendarEntry(selectedStartDate);
    const startOfDay: moment.Moment = moment(day).startOf('day');

    if (
      startOfDay.isBefore(moment(selectedStartDate).add(startCalendarEntry?.minLengthOfStay, 'day'))
    ) {
      return true;
    }

    // anything after first no avail after start date
    if (startOfDay.isAfter(getFirstMissingDay(moment(selectedStartDate)))) {
      return true;
    }

    // anything after start date + max stay
    if (
      startCalendarEntry?.maxLengthOfStay !== 0 &&
      startOfDay.isAfter(moment(selectedStartDate).add(startCalendarEntry?.maxLengthOfStay, 'day'))
    ) {
      return true;
    }

    return calendarEntry?.closedToDeparture;
  };

  const isDayBlocked: (day: moment.Moment) => boolean = (day: moment.Moment) => {
    const calendarEntry: ICalendarEntry = getCalendarEntry(day);

    if (focusedDate === 'startDate') {
      return isStartDateDisabled(calendarEntry, day);
      // remove invalid end dates
    } else if (focusedDate === 'endDate') {
      return isEndDateDisabled(calendarEntry, day);
    } else {
      return true;
    }
  };

  const getFirstMissingDay: (selectedStartDay: moment.Moment) => moment.Moment = (
    selectedStartDay: moment.Moment
  ) => {
    let missingDay: moment.Moment = null;
    while (!missingDay) {
      const calendarEntry: ICalendarEntry = getCalendarEntry(selectedStartDay);
      if (!calendarEntry || calendarEntry.checkOutDayOnly) {
        missingDay = moment(selectedStartDay);
      }
      selectedStartDay.add(1, 'days');
    }

    return missingDay;
  };

  const showPrices: (day: moment.Moment) => string | JSX.Element = (day: moment.Moment) => {
    const currentDayEntry: ICalendarEntry = getCalendarEntry(day);

    return (
      <div>
        <div>{day.format('D')}</div>
        <ResponsiveTypography color={'inherit'} variant={ResponsiveTypographyVariant.nano}>
          {currentDayEntry && currentDayEntry.rates.rate.forDisplay}
        </ResponsiveTypography>
      </div>
    );
  };

  const renderCalendarInfo: () => JSX.Element = () => (
    <CalendarInfo
      defaultDateSelected={defaultDateSelected}
      endDate={endDateDraft}
      disabled={!(checkInLabel && checkOutLabel)}
      handleShowDeals={handleShowDeals}
      handleCancel={handleCancel}
      isLoadingDraftDeals={false}
      translations={translations}
      theme={theme}
      calendarSelectionSummary={calendarSelectionSummary}
      rooms={rooms}
    />
  );

  return (
    <Container
      className={className}
      data-track-action="booking-amendment-calendar-interaction"
      data-track-label="dates"
      data-testid="bookingAmendments"
    >
      {!isMobile && (
        <TitleContainer variant={ResponsiveTypographyVariant.t4Serif}>
          {i18n('amendments.select.new.dates', [], translations)}
        </TitleContainer>
      )}
      <TextInputContainer>
        <BookingAmendmentTextInputWithMargin
          utilityIcon={IconName.calendar}
          label={i18n('common.checkIn.label', [], translations)}
          placeholderText={i18n(
            'common.date.dayMonthYearSlashSeperated',
            [],
            translations
          ).toLowerCase()}
          value={checkInLabel}
          actionIcon={travelPeriodValueLabel && IconName.close}
          onActionClick={handleResetTravelPeriod}
        />
        <BookingAmendmentTextInput
          utilityIcon={IconName.calendar}
          label={i18n('common.checkOut.label', [], translations)}
          placeholderText={i18n(
            'common.date.dayMonthYearSlashSeperated',
            [],
            translations
          ).toLowerCase()}
          value={checkOutLabel}
          actionIcon={travelPeriodValueLabel && IconName.close}
          onActionClick={handleResetTravelPeriod}
        />
      </TextInputContainer>
      <CalendarContainer>
        <InlineDateRangePicker
          renderCalendarInfo={renderCalendarInfo}
          onDatesChange={handleDatesChange}
          focusedInput={focusedDate}
          startDate={
            startDateDraft
              ? moment(startDateDraft).locale(locale)
              : defaultDateSelected
              ? originalStartDate
              : null
          }
          endDate={
            endDateDraft
              ? moment(endDateDraft).locale(locale)
              : defaultDateSelected
              ? originalEndDate
              : null
          }
          onFocusChange={handleFocusChange}
          noBorder={true}
          locale={locale}
          renderDayContents={showPrices}
          minDate={returnFirstAvailableMonth()}
          maxDate={returnLastAvailableMonth()}
          calendarScrollerStyles={css`
            padding-top: 100px;
            padding-bottom: 350px;
          `}
          initialVisibleMonth={returnFirstAvailableMonth}
          daySize={isDesktop ? CALENDAR_DAY_SIZE_DESKTOP : CALENDAR_DAY_SIZE_MOBILE}
          isDayBlocked={isDayBlocked}
        />
      </CalendarContainer>
    </Container>
  );
};

export default withTheme(BookingAmendments);
